/**
 * The two pictures side by side (expert feedback round 3, D-025): «Сейчас» and «После лечения», each a full
 * scene. The WebGL pipeline renders both panes; each is copied into its own 2D canvas. Distance tags, crosshair,
 * zoom and the dark-adaptation caption live on top of the pictures.
 */

import { FEATURES } from '../features';
import { t } from '../i18n';
import { DARK_DEMO_MINUTES } from '../optics/adaptation';
import { gazeRelevant } from '../optics/retina';
import { clamp } from '../optics/types';
import { getScene } from '../scenes';
import { RENDER_H, RENDER_W } from '../scenes/types';
import { state, update } from '../state';
import { h } from './dom';

export type Side = 0 | 1;

export interface Views {
  left: HTMLElement;
  right: HTMLElement;
  sync(): void;
  setBusy(busy: boolean): void;
  /** Per-image magnification (aphakic glasses) so the distance tags follow the picture. */
  setMagnification(left: number, right: number): void;
  /** Dark-adaptation demo: simulated minutes, or null when the demo is off. */
  setDemo(minutes: number | null): void;
  /** Name of the chosen treatment, shown on the right picture. */
  setAfterLabel(text: string): void;
  /** Copy the WebGL canvas (holding one rendered pane) into a picture. */
  blit(side: Side, source: HTMLCanvasElement): void;
}

interface View {
  el: HTMLElement;
  scroller: HTMLElement;
  inner: HTMLElement;
  ctx: CanvasRenderingContext2D;
  badge: HTMLElement;
  badgeSub: HTMLElement;
  tags: HTMLElement;
  tagSpans: { span: HTMLSpanElement; fx: number; fy: number }[];
  crosshair: HTMLElement;
  spinner: HTMLElement;
}

function buildView(side: Side): View {
  const canvas = h('canvas', { width: RENDER_W, height: RENDER_H, class: side === 0 ? 'pic pic-now' : 'pic pic-after' });
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  const tags = h('div', { class: 'tags', 'aria-hidden': 'true' });
  const crosshair = h('div', { class: 'crosshair', 'aria-hidden': 'true', hidden: true });
  const inner = h('div', { class: 'stage-inner' }, canvas, tags, crosshair);
  const scroller = h('div', { class: 'scroller' }, inner);
  const badgeSub = h('span', { class: 'badge-sub' });
  const badge = h('div', { class: `badge ${side === 0 ? 'badge-now' : 'badge-after'}` }, h('span', { class: 'badge-main' }), badgeSub);
  const spinner = h('div', { class: 'spinner', role: 'status', hidden: true });
  const el = h('div', { class: `view ${side === 0 ? 'view-now' : 'view-after'}` }, scroller, badge, spinner);
  return { el, scroller, inner, ctx, badge, badgeSub, tags, tagSpans: [], crosshair, spinner };
}

export function buildViews(): Views {
  const lang = state.lang;
  const views: [View, View] = [buildView(0), buildView(1)];
  const zoomHint = h('div', { class: 'zoom-hint', text: t(lang, 'zoom.hint'), hidden: true });
  views[1].el.append(zoomHint);
  const demoText = h('div', { class: 'demo-text' });
  const demoFill = h('i');
  const demoCaption = h('div', { class: 'demo-caption', role: 'status', hidden: true },
    demoText, h('div', { class: 'demo-bar' }, demoFill));
  views[1].el.append(demoCaption);

  // Gaze follow: the fixation point tracks the pointer over either picture (render px, y-up).
  for (const v of views) {
    v.inner.addEventListener('pointermove', (e) => {
      if (!state.gaze) return;
      const r = v.inner.getBoundingClientRect();
      const x = clamp((e.clientX - r.left) / r.width, 0, 1) * RENDER_W;
      const y = (1 - clamp((e.clientY - r.top) / r.height, 0, 1)) * RENDER_H;
      update({ fixX: Math.round(x), fixY: Math.round(y) });
    });
  }
  // In zoom mode the two pictures scroll together, so the same detail stays side by side.
  let syncing = false;
  views.forEach((v, i) => {
    v.scroller.addEventListener('scroll', () => {
      if (syncing) return;
      syncing = true;
      const other = views[1 - i]!;
      other.scroller.scrollLeft = v.scroller.scrollLeft;
      other.scroller.scrollTop = v.scroller.scrollTop;
      requestAnimationFrame(() => {
        syncing = false;
      });
    });
  });

  let mags: [number, number] = [1, 1];
  let tagsKey = '';
  let demoMinutes: number | null = null;
  let afterLabel = '';

  function syncTags(): void {
    const show = FEATURES.tags && state.tags;
    const scene = getScene(state.scene);
    const key = `${scene.id}|${state.lang}`;
    views.forEach((v, side) => {
      v.tags.hidden = !show;
      if (!show) return;
      if (key !== tagsKey) {
        const ppd = RENDER_W / scene.hfov;
        v.tagSpans = scene.tags.map((tag) => ({
          span: h('span', { class: 'tag', text: t(state.lang, tag.key) }),
          fx: (RENDER_W / 2 + tag.x * ppd) / RENDER_W,
          fy: (RENDER_H / 2 - tag.y * ppd) / RENDER_H,
        }));
        v.tags.replaceChildren(...v.tagSpans.map((s) => s.span));
      }
      // A magnified picture moves objects away from the centre; place the tag where the object appears.
      const m = mags[side] ?? 1;
      for (const s of v.tagSpans) {
        const x = 0.5 + (s.fx - 0.5) * m;
        const y = 0.5 + (s.fy - 0.5) * m;
        s.span.hidden = x < 0.02 || x > 0.98 || y < 0.02 || y > 0.98;
        s.span.style.left = `${(x * 100).toFixed(2)}%`;
        s.span.style.top = `${(y * 100).toFixed(2)}%`;
      }
    });
    if (show) tagsKey = key;
  }

  function syncBadges(): void {
    const [now, after] = views;
    const nowMain = now.badge.querySelector('.badge-main')!;
    const afterMain = after.badge.querySelector('.badge-main')!;
    if (demoMinutes !== null) {
      nowMain.textContent = t(state.lang, 'demo.left');
      afterMain.textContent = t(state.lang, 'demo.right');
      now.badgeSub.textContent = '';
      after.badgeSub.textContent = '';
      return;
    }
    nowMain.textContent = t(state.lang, 'badge.now');
    afterMain.textContent = t(state.lang, 'badge.after');
    now.badgeSub.textContent = '';
    after.badgeSub.textContent = afterLabel;
  }

  function sync(): void {
    const zoomed = FEATURES.zoom && state.zoom;
    const showCross = state.gaze && gazeRelevant(state);
    for (const v of views) {
      v.el.classList.toggle('zoomed', zoomed);
      v.crosshair.hidden = !showCross;
      v.crosshair.style.left = `${((state.fixX / RENDER_W) * 100).toFixed(2)}%`;
      v.crosshair.style.top = `${((1 - state.fixY / RENDER_H) * 100).toFixed(2)}%`;
    }
    zoomHint.hidden = !zoomed;
    syncTags();
    syncBadges();
  }

  sync();
  return {
    left: views[0].el,
    right: views[1].el,
    sync,
    setBusy(busy) {
      for (const v of views) v.spinner.hidden = !busy;
    },
    setMagnification(left, right) {
      if (left === mags[0] && right === mags[1]) return;
      mags = [left, right];
      syncTags();
    },
    setDemo(minutes) {
      demoMinutes = minutes;
      demoCaption.hidden = minutes === null;
      if (minutes !== null) {
        demoText.textContent = t(state.lang, 'demo.caption', { x: Math.round(minutes) });
        demoFill.style.width = `${((minutes / DARK_DEMO_MINUTES) * 100).toFixed(1)}%`;
      }
      syncBadges();
    },
    setAfterLabel(text) {
      if (text === afterLabel) return;
      afterLabel = text;
      syncBadges();
    },
    blit(side, source) {
      views[side].ctx.drawImage(source, 0, 0);
    },
  };
}
